import { firebasestore, firestore } from "../firebase/firebase";


/**
 * Gets history data of a given cryptocurrency via firestore and Cryptowatch API intelligently.
 * @param  {Object} curr The object of current cryptocurrency.
 * @param  {React.Dispatch<Array>} setdata The React state update function for final data.
 * @param  {React.Dispatch<Boolean>} setShow The React state update function for boolean flag of content to be shown or not.
 * @param  {Boolean} full Whether to get complete data of data of last 30 days.
 * @param  {Boolean} olhc Whether to get olhc data for olhc graph or single point data for line plot.
 */
export function GetHistory(curr, setdata, setShow, full = false, olhc = false) {
    Promise.all([
        firestore.collection('History').doc(curr.info.symbol).get(),
        firestore.collection('History').doc(curr.info.symbol).collection('Timings').where('active', '==', true).orderBy('datapoints', "desc").limit(1).get()
    ])  // Get Symbol timing, Get symbol history from exchange with most number of datapoints.
        .then(([sym, docs]) => {
            if (!docs.empty) { // If no active exchange is present in dataset
                //console.log("Document Symbol:", curr.market+':'+curr.pair, "Document data:", doc.data());
                docs.forEach((doc) => {     // For each doc in Query Snapshot (which has only one doc.)
                    if ((firebasestore.Timestamp.now().seconds - doc.data().last.seconds) <= 86400) {   // Has data been updated within one day?
                        firestore.collection('History').doc(curr.info.symbol).collection(doc.id).where(firebasestore.FieldPath.documentId(), ">=", (full ? 0 : (doc.data().last.seconds - 7776000)).toString()).get() // Get buckets containing data of 90 days.
                            .then((querySnapshot) => {
                                let d = []; // final data array
                                querySnapshot.forEach((dataPoint) => {  // For each bucket
                                    // dataPoint.data() is never undefined for query doc snapshots
                                    //console.log(dataPoint.id, " => ", dataPoint.data();
                                    dataPoint.data().content.forEach((val) => { // Add data of bucket into array d;
                                        let dt = new Date(val.CloseTime * 1000);
                                        d.push(olhc ? [dt, val.LowPrice, val.OpenPrice, val.ClosePrice, val.HighPrice] : [dt, val.ClosePrice]);
                                    });
                                });
                                // console.log('d from firestore', curr.info.symbol, doc.id, d);
                                let D = full ? d : d.slice((d.length > 31 ? d.length - 31 : 0)); // Take data of last 30 days.
                                D.unshift(olhc ? ['Date', 'L-H, O-C', 'Open', 'Close', 'High'] : ['Date', 'Price']);    // Data formatting for google charts.
                                setdata(D);
                            });
                    } else {    // If not updated within a day, We will have to get new data from API, Merge it to database and show the data.
                        Promise.all([firestore.collection('History').doc(curr.info.symbol).collection(doc.id).where(firebasestore.FieldPath.documentId(), ">=", (full ? 0 : (doc.data().last.seconds - 7776000)).toString()).get(), fetch('https://cors-proxy-go.onrender.com/https://api.cryptowat.ch/markets/' + doc.id + '/' + curr.pair + '/ohlc?periods=86400&after=' + (doc.data().last.seconds + 1000))])
                            // Get data of last 90 days which is present in the dataset. Get data from API which is not there in the dataset.
                            .then(([dFromStore, dFromApi]) => Promise.all([dFromStore, dFromApi.json()]))
                            .then(([dFromStore, dFromApi]) => {
                                if (dFromApi.result) {
                                    let bucket; // last bucket
                                    let d = [];
                                    dFromStore.forEach((dataPoint) => {
                                        // dataPoint.data() is never undefined for query doc snapshots
                                        //console.log(dataPoint.id, " => ", dataPoint.data();
                                        bucket = dataPoint;
                                        dataPoint.data().content.forEach((val) => {     // For Each Bucket, add data to array d;
                                            let dt = new Date(val.CloseTime * 1000);
                                            d.push(olhc ? [dt, val.LowPrice, val.OpenPrice, val.ClosePrice, val.HighPrice] : [dt, val.ClosePrice]);
                                        });
                                    });

                                    if ((firebasestore.Timestamp.now().seconds - dFromApi.result[86400][dFromApi.result[86400].length - 1][0]) <= 10000) {  // If we got updated data from API.
                                        dFromApi.result[86400].filter(x => x[0] >= (firebasestore.Timestamp.now().seconds - 2678400)).forEach((arr) => {    // Add data of last 31 days. (last 30 days + current 1 day)
                                            let dt = new Date(arr[0] * 1000);
                                            //console.log('date:', Math.floor(dt.getTime() / 1000), arr[0]);
                                            d.push(olhc ? [dt, arr[3], arr[1], arr[4], arr[2]] : [dt, arr[4]]);
                                        });
                                        d = d.slice(0, d.length - 1);   // remove the data of current day, since it is not final.
                                        // console.log('d from firestore and API', curr.info.symbol, doc.id, d);
                                        // console.log('dataFrom Store: ', dFromStore.docs.map(dat => dat.data().content), 'dataFrom API: ', dFromApi);
                                        d = full ? d : d.slice((d.length > 31 ? d.length - 31 : 0)); // Only keep data of 30 days.
                                        d.unshift(olhc ? ['Date', 'L-H, O-C', 'Open', 'Close', 'High'] : ['Date', 'Price']);    // Data formatting for google charts.
                                        setdata(d);
                                    } else {
                                        setShow(false);
                                    }

                                    // Start Uploading to firestore.
                                    let batch = firestore.batch();  // Upload data in Batch.
                                    let count = bucket.data().content.length; // days count in last bucket.
                                    let v = bucket.data().content; // vector with content of 60 days
                                    let flag = false;
                                    let first = bucket.id;
                                    if (bucket.data().content.length == 60) {
                                        count = 0; // days count
                                        v = []; // vector with content of 60 days
                                        flag = false;
                                        first = dFromApi.result[86400][0][0];
                                    }
                                    let it = 0;
                                    while (it !== dFromApi.result[86400].length - 1) {
                                        let arr = dFromApi.result[86400][it];
                                        if (flag) {
                                            first = arr[0];
                                            flag = false;
                                        }
                                        v.push({
                                            CloseTime: arr[0],
                                            OpenPrice: arr[1],
                                            HighPrice: arr[2],
                                            LowPrice: arr[3],
                                            ClosePrice: arr[4],
                                            Volume: arr[5],
                                            QuoteVolume: arr[6]
                                        });
                                        if (++count == 60) {
                                            batch.set(firestore.collection('History').doc(curr.info.symbol).collection(doc.id).doc(first.toString()), {
                                                content: v,
                                            }, { merge: true });
                                            flag = true;
                                            count = 0;
                                            v = [];
                                        }
                                        it++;
                                    }

                                    if (it !== 0) {
                                        if (v.length !== 0) {
                                            batch.set(firestore.collection('History').doc(curr.info.symbol).collection(doc.id).doc(first.toString()), {
                                                content: v,
                                            }, { merge: true });
                                        }

                                        let isUpdated = (firebasestore.Timestamp.now().seconds - dFromApi.result[86400][dFromApi.result[86400].length - 1][0]) <= 1296000; // wait for 15 days. If no data is found thereafter, Mark endpoint as inactive.


                                        //if(!dFromApi.result[86400][it - 1]) console.log("Wanted dt", dFromApi.result[86400], it, dFromApi.result[86400][it - 1]);
                                        batch.set(firestore.collection('History').doc(curr.info.symbol).collection('Timings').doc(doc.id), {
                                            last: firebasestore.Timestamp.fromMillis(dFromApi.result[86400][it - 1][0] * 1000),
                                            active: isUpdated,
                                            datapoints: firebasestore.FieldValue.increment(dFromApi.result[86400].length),
                                        }, { merge: true });

                                        if (!isUpdated) {   // If endpoint is inactive. Add it in inactive endpoints collection for further review.
                                            batch.set(firestore.collection('Inactive Endpoints').doc(curr.info.symbol + ':' + doc.id), {
                                                setOn: firebasestore.Timestamp.now(),
                                            }, { merge: true });
                                        }

                                        batch.commit()
                                            .then(() => {
                                                // console.log("Data updated for " + doc.id + ":" + curr.info.symbol);
                                            }).catch((err) => console.log("Error while data updating for " + doc.id + ":" + curr.info.symbol, err));

                                        // console.log('Hey', curr.info.symbol, doc.id);
                                    }
                                } else { // If API is giving error.
                                    setShow(false);
                                    // console.log("Cryptowatch Error : ", dFromApi);
                                }
                            });
                    }
                });
            } else if (!sym.exists) {   // New cryptocurrency found. Show data from API and upload data in database.
                // doc.data() will be undefined in this case
                // console.log("No", curr.market + ':' + curr.pair, "document!");

                let last = firebasestore.Timestamp.now().seconds + 172800;
                let isDataPresent = false; // Does any exchange has some history data?
                async function getHistory(market) {
                    let res = fetch('https://cors-proxy-go.onrender.com/https://api.cryptowat.ch/markets/' + market + '/' + curr.pair + '/ohlc?periods=86400').then((result) => result.json());
                    res = await res;
                    // console.log("Yeiks...", market, curr.pair, res.result);
                    if (res.result) {
                        // console.log('Yey..', (firebasestore.Timestamp.now().seconds - res.result[86400][res.result[86400].length - 1][0]) <= 86400, res.result[86400][0][0], last, res.result[86400][0][0] < last);
                        if ((firebasestore.Timestamp.now().seconds - res.result[86400][res.result[86400].length - 1][0]) <= 86400 && res.result[86400][0][0] < last) {

                            let d = [];
                            last = res.result[86400][0][0];
                            res.result[86400].filter(x => x[0] >= (firebasestore.Timestamp.now().seconds - 2592000)).map((arr) => {
                                let dt = new Date(arr[0] * 1000);
                                //console.log('date:', Math.floor(dt.getTime() / 1000), arr[0]);
                                d.push(olhc ? [dt, arr[3], arr[1], arr[4], arr[2]] : [dt, arr[4]]);
                            });
                            d = d.slice(0, d.length - 1);
                            // console.log('d from API', market, curr.pair, d);
                            d = full ? d : d.slice((d.length > 31 ? d.length - 31 : 0));
                            d.unshift(olhc ? ['Date', 'L-H, O-C', 'Open', 'Close', 'High'] : ['Date', 'Price']);
                            setdata(d);
                            isDataPresent = true;
                        }
                        //testBatchedWrites(res.result[86400], curr.info.symbol, market).then(() => console.log("Completed!!"), (err) => console.log(err));
                        
                        // Start Uploading to firestore.
                        let batch = firestore.batch();
                        let count = 0; // days count
                        let v = []; // vector with content of 60 days
                        let flag = false;
                        let first = res.result[86400][0][0];
                        let it = 0;
                        while (it !== res.result[86400].length - 1) {
                            let arr = res.result[86400][it];
                            if (flag) {
                                first = arr[0];
                                flag = false;
                            }
                            v.push({
                                CloseTime: arr[0],
                                OpenPrice: arr[1],
                                HighPrice: arr[2],
                                LowPrice: arr[3],
                                ClosePrice: arr[4],
                                Volume: arr[5],
                                QuoteVolume: arr[6]
                            });
                            if (++count == 60) {
                                batch.set(firestore.collection('History').doc(curr.info.symbol).collection(market).doc(first.toString()), {
                                    content: v,
                                });
                                flag = true;
                                count = 0;
                                v = [];
                            }
                            it++;
                        }

                        if (v.length !== 0) {
                            batch.set(firestore.collection('History').doc(curr.info.symbol).collection(market).doc(first.toString()), {
                                content: v,
                            });
                        }

                        let isUpdated = (firebasestore.Timestamp.now().seconds - res.result[86400][res.result[86400].length - 1][0]) <= 1296000; // wait for 15 days.

                        batch.set(firestore.collection('History').doc(curr.info.symbol).collection('Timings').doc(market), {
                            first: firebasestore.Timestamp.fromMillis(res.result[86400][0][0] * 1000),
                            last: firebasestore.Timestamp.fromMillis(res.result[86400][it - 1][0] * 1000),
                            datapoints: res.result[86400].length,
                            active: isUpdated,
                        });

                        if (!isUpdated) {
                            batch.set(firestore.collection('Inactive Endpoints').doc(curr.info.symbol + ':' + market), {
                                setOn: firebasestore.Timestamp.now(),
                            });
                        }

                        return await batch.commit();
                    }
                }

                Promise.allSettled(curr.market.map((market) => getHistory(market)))
                    .then(() => {
                        if (isDataPresent) {
                            // console.log('Data Uploaded for ', curr.info.symbol);
                        } else {
                            setShow(false);
                            // console.log(curr.info.symbol + " will not be shown..");
                        }
                    });
            } else {
                setShow(false);
                // console.log(curr.info.symbol + " will not be shown..");
            }
        }).catch((err) => console.log(err));
}
